import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, X, Filter, ShoppingBag, Heart, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [results, setResults] = useState([]);
  const [correctedQuery, setCorrectedQuery] = useState(null);
  const [usedFuzzy, setUsedFuzzy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  const addItem = useCartStore(state => state.addItem);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const toggleWishlist = useWishlistStore(state => state.toggleWishlist);
  const isInWishlist = useWishlistStore(state => state.isInWishlist);

  // Load categories and recent searches on mount
  useEffect(() => {
    fetchCategories();
    loadRecentSearches();
    if (initialQuery) {
      performSearch(initialQuery);
    }
    // Focus search input
    searchInputRef.current?.focus();
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories?hide_empty=true');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const loadRecentSearches = () => {
    const saved = localStorage.getItem('recent-searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  };

  const saveRecentSearch = (searchTerm) => {
    if (!searchTerm.trim()) return;
    const saved = localStorage.getItem('recent-searches');
    let searches = saved ? JSON.parse(saved) : [];
    searches = [searchTerm, ...searches.filter(s => s !== searchTerm)].slice(0, 10);
    localStorage.setItem('recent-searches', JSON.stringify(searches));
    setRecentSearches(searches.slice(0, 5));
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('recent-searches');
    setRecentSearches([]);
  };

  // Debounced search for suggestions
  const fetchSuggestions = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/products?search=${encodeURIComponent(searchTerm)}&limit=6`);
        setSuggestions(response.data || []);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }, 300),
    []
  );

  const performSearch = async (searchTerm, category = selectedCategory) => {
    if (!searchTerm.trim() && !category) {
      setResults([]);
      setCorrectedQuery(null);
      setUsedFuzzy(false);
      return;
    }

    setLoading(true);
    setShowSuggestions(false);

    try {
      // Smart-Search-Endpoint mit Tippfehler-Erkennung
      const params = new URLSearchParams();
      if (searchTerm.trim()) {
        params.set('q', searchTerm);
        saveRecentSearch(searchTerm);
      }
      if (category) params.set('category', category);
      params.set('limit', '60');

      const response = await axiosInstance.get(`/search?${params.toString()}`);
      const data = response.data || {};
      setResults(data.results || []);
      setCorrectedQuery(data.corrected_query || null);
      setUsedFuzzy(!!data.used_fuzzy);

      // Update URL
      const urlParams = new URLSearchParams();
      if (searchTerm.trim()) urlParams.set('q', searchTerm);
      if (category) urlParams.set('category', category);
      setSearchParams(urlParams);

    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);
    fetchSuggestions(value);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    performSearch(query);
  };

  const handleSuggestionClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    performSearch(query, category);
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!addItem(product)) return;
    toast.success(`${product.name} zum Warenkorb hinzugefügt`);
  };

  const handleToggleWishlist = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Bitte melden Sie sich an');
      return;
    }
    await toggleWishlist(productId);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
    setSelectedCategory('');
    setSearchParams({});
    searchInputRef.current?.focus();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0EB' }}>
      {/* Search Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <form onSubmit={handleSearch} className="relative" ref={suggestionsRef}>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400" />
              <Input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Produkte suchen..."
                className="w-full pl-14 pr-24 py-6 text-lg rounded-full border-2 border-[#C4A084] focus:border-[#8B7355] focus:ring-[#8B7355]"
                data-testid="search-input"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
                {query && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSearch}
                    className="rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                )}
                <Button
                  type="submit"
                  className="rounded-full px-6"
                  style={{ backgroundColor: '#8B7355' }}
                  data-testid="search-submit"
                >
                  Suchen
                </Button>
              </div>
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && (suggestions.length > 0 || recentSearches.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50">
                {/* Recent Searches */}
                {!query && recentSearches.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-500">Letzte Suchen</span>
                      <button
                        onClick={clearRecentSearches}
                        className="text-xs text-[#8B7355] hover:underline"
                      >
                        Löschen
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((term, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setQuery(term);
                            performSearch(term);
                          }}
                          className="px-3 py-1 bg-gray-100 rounded-full text-sm hover:bg-[#E8E0D5] transition-colors"
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Suggestions */}
                {suggestions.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {suggestions.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSuggestionClick(product)}
                        className="w-full flex items-center gap-4 p-4 hover:bg-[#F5F0EB] transition-colors text-left"
                        data-testid={`suggestion-${product.id}`}
                      >
                        {product.images && product.images[0] ? (
                          <img
                            src={imgVariant(product.images[0], { w: 400 })}
                            alt={product.name}
                            className="w-14 h-14 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                            <ShoppingBag className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.category}</p>
                        </div>
                        <span className="font-bold text-[#6D5A47]">
                          {formatEUR(product.price)}
                          {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                            <span className="text-xs font-normal">/{product.price_quantity}St.</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>

          {/* Category Filter Pills */}
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex-shrink-0"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
              <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            
            <div className="h-6 w-px bg-gray-300" />
            
            <button
              onClick={() => handleCategorySelect('')}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-[#8B7355] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
            
            {categories.slice(0, 6).map((cat) => {
              const categoryName = typeof cat === 'string' ? cat : cat.name;
              return (
                <button
                  key={categoryName}
                  onClick={() => handleCategorySelect(categoryName)}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === categoryName
                      ? 'bg-[#8B7355] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {categoryName}
                </button>
              );
            })}
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-[#F5F0EB] rounded-xl">
              <h4 className="font-medium mb-3">Alle Kategorien</h4>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const categoryName = typeof cat === 'string' ? cat : cat.name;
                  return (
                    <button
                      key={categoryName}
                      onClick={() => handleCategorySelect(categoryName)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        selectedCategory === categoryName
                          ? 'bg-[#8B7355] text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {categoryName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355]"></div>
          </div>
        ) : results.length > 0 ? (
          <>
            {/* Fuzzy-Hinweis: „Meintest du …?" oder „Ergebnisse für …" */}
            {usedFuzzy && correctedQuery && (
              <div className="mb-4 p-3 sm:p-4 border-l-4 border-[#C4A084] bg-[#FAF5EE] text-[#5D4E3C]" data-testid="search-corrected-banner">
                <p className="text-sm">
                  Keine exakte Übereinstimmung für <span className="font-semibold">„{query}"</span>.
                  Meintest du:{' '}
                  <button
                    type="button"
                    onClick={() => { setQuery(correctedQuery); performSearch(correctedQuery); }}
                    className="font-bold underline text-[#5D4E3C] hover:text-[#3D2F22]"
                    data-testid="search-corrected-link"
                  >
                    {correctedQuery}
                  </button>
                  ?
                </p>
                <p className="text-xs text-[#8B7355] mt-1">
                  Unten siehst du die wahrscheinlichsten Treffer ({results.length}).
                </p>
              </div>
            )}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                {results.length} Ergebnis{results.length !== 1 ? 'se' : ''}
                {query && <span className="text-gray-500"> für "{query}"</span>}
                {selectedCategory && <span className="text-gray-500"> in {selectedCategory}</span>}
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="search-results">
              {results.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                  data-testid={`result-${product.id}`}
                >
                  <div className="aspect-square relative overflow-hidden">
                    {product.images && product.images[0] ? (
                      <img
                        src={imgVariant(product.images[0], { w: 400 })}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <ShoppingBag className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Wishlist Button */}
                    <button
                      onClick={(e) => handleToggleWishlist(product.id, e)}
                      className={`absolute top-2 right-2 p-2 rounded-full transition-colors ${
                        isInWishlist(product.id)
                          ? 'bg-red-500 text-white'
                          : 'bg-white/80 text-gray-600 hover:bg-white'
                      }`}
                    >
                      <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                  
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1 group-hover:text-[#8B7355]">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">{product.category}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#6D5A47]">
                        {formatEUR(product.price)}
                        {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                          <span className="text-xs font-normal">/{product.price_quantity}St.</span>
                        )}
                      </span>
                      <Button
                        size="sm"
                        onClick={(e) => handleAddToCart(product, e)}
                        disabled={!(product.stock > 0)}
                        className="text-xs px-3"
                        style={{ backgroundColor: '#8B7355' }}
                        data-testid={`search-add-to-cart-${product.id}`}
                      >
                        <ShoppingBag className="h-3 w-3 mr-1" />
                        {product.stock > 0 ? 'Kaufen' : 'Ausverkauft'}
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : query || selectedCategory ? (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Keine Ergebnisse gefunden</h2>
            <p className="text-gray-500 mb-6">
              Versuchen Sie einen anderen Suchbegriff oder wählen Sie eine andere Kategorie.
            </p>
            {correctedQuery && (
              <div className="max-w-md mx-auto mb-6 p-4 bg-[#FAF5EE] border border-[#C4A084]" data-testid="search-no-results-suggestion">
                <p className="text-sm text-[#5D4E3C]">
                  Meintest du vielleicht:
                </p>
                <button
                  type="button"
                  onClick={() => { setQuery(correctedQuery); performSearch(correctedQuery); }}
                  className="mt-1 text-lg font-bold text-[#5D4E3C] underline hover:text-[#3D2F22]"
                  data-testid="search-no-results-suggestion-link"
                >
                  {correctedQuery}
                </button>
              </div>
            )}
            <Button onClick={clearSearch} variant="outline">
              Suche zurücksetzen
            </Button>
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-[#C4A084] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Wonach suchen Sie?</h2>
            <p className="text-gray-500">
              Geben Sie einen Suchbegriff ein oder wählen Sie eine Kategorie.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Debounce helper
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default SearchPage;
